import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from '@tanstack/react-router';
import { useHomeTasks } from '../hooks/useHomeTasks';
import { TaskItem } from './TaskItem';
import { Skeleton } from '@/components/ui/skeleton';
import type { StaticTask, Task } from '../types/home-tasks.types';
import { URGENCY_COLORS, URGENCY_ORDER } from '../types/home-tasks.types';

/**
 * Static placeholder tasks (for demo purposes)
 * These will be replaced with real data as more task types are implemented
 */
const STATIC_TASKS: StaticTask[] = [
  {
    id: 'static-inventory-microchip',
    title: 'Restock materials for Microchip demand',
    urgency: 'high',
    done: false,
    targetPath: '/procurement-demo/inventory',
  },
  {
    id: 'static-2',
    title: 'Approve PO for Q2 supplies',
    urgency: 'high',
    done: false,
  },
  {
    id: 'static-3',
    title: 'Update rebate agreement terms',
    urgency: 'high',
    done: true,
  },
  {
    id: 'static-4',
    title: 'Process inbound shipment',
    urgency: 'normal',
    done: false,
  },
  {
    id: 'static-5',
    title: 'Check vendor payment status',
    urgency: 'normal',
    done: false,
  },
  {
    id: 'static-6',
    title: 'Archive completed orders',
    urgency: 'low',
    done: true,
  },
  {
    id: 'static-rebate-1',
    title: 'Review Lumina - Rebate Agreement 2026',
    urgency: 'urgent',
    done: false,
    targetPath: '/rebates/agreements',
  },
  {
    id: 'static-rebate-2',
    title: 'Process rebate collection for NexiPoint Logistics',
    urgency: 'high',
    done: false,
    targetPath: '/rebates/debtors',
  },
  {
    id: 'static-rebate-3',
    title: 'Verify forecast calculations for Stratos Manufacturing',
    urgency: 'high',
    done: false,
    targetPath: '/rebates/analytics',
  },
  {
    id: 'static-rebate-4',
    title: 'Review debtors report for Lumina Q4 2025',
    urgency: 'normal',
    done: false,
    targetPath: '/rebates/debtors',
  },
  {
    id: 'static-rebate-5',
    title: 'Finalize Aetheria Solutions - Rebate Agreement 2026',
    urgency: 'normal',
    done: false,
    targetPath: '/rebates/agreements',
  },
  {
    id: 'static-rebate-6',
    title: 'Export rebate data for month-end reconciliation',
    urgency: 'low',
    done: false,
    targetPath: '/rebates/data-export',
  },
];

type CombinedTask =
  | { kind: 'dynamic'; task: Task }
  | { kind: 'static'; task: StaticTask };

/**
 * Static task item component
 */
function StaticTaskItem({
  task,
  onNavigate,
}: {
  task: StaticTask;
  onNavigate?: (path: string) => void;
}) {
  const handleClick = () => {
    if (task.targetPath && onNavigate) {
      onNavigate(task.targetPath);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && task.targetPath && onNavigate) {
      e.preventDefault();
      onNavigate(task.targetPath);
    }
  };

  return (
    <div
      role={task.targetPath ? 'button' : undefined}
      tabIndex={task.targetPath ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-stone-100 cursor-pointer"
    >
      <span
        className={cn(
          'w-2 h-2 rounded-full shrink-0',
          URGENCY_COLORS[task.urgency],
        )}
      />
      <span
        className={cn(
          'text-sm truncate',
          task.done && 'line-through text-muted-foreground',
        )}
      >
        {task.title}
      </span>
    </div>
  );
}

/**
 * Task list container component
 * Combines dynamic and static tasks into a single urgency-sorted list
 */
export function TaskList() {
  const { tasks, isLoading, error, handleTaskClick } = useHomeTasks();
  const navigate = useNavigate();

  const handleStaticTaskNavigate = (path: string) => {
    navigate({ to: path });
  };

  // Combine and sort dynamic + static tasks by urgency
  const combinedTasks = useMemo((): CombinedTask[] => {
    const items: CombinedTask[] = [
      ...tasks.map((task): CombinedTask => ({ kind: 'dynamic', task })),
      ...STATIC_TASKS.map((task): CombinedTask => ({ kind: 'static', task })),
    ];
    items.sort(
      (a, b) => URGENCY_ORDER[a.task.urgency] - URGENCY_ORDER[b.task.urgency],
    );
    return items;
  }, [tasks]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center gap-2 py-1.5 px-2">
            <Skeleton className="w-2 h-2 rounded-full" />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-muted-foreground px-2 py-2">
        Failed to load tasks
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {combinedTasks.map((item) =>
        item.kind === 'dynamic' ? (
          <TaskItem
            key={item.task.id}
            task={item.task}
            onClick={() => handleTaskClick(item.task)}
          />
        ) : (
          <StaticTaskItem
            key={item.task.id}
            task={item.task}
            onNavigate={handleStaticTaskNavigate}
          />
        ),
      )}
    </div>
  );
}
